import { ConditionFieldDefinition } from '../config/conditionSearch';

type JsonSchema = Record<string, any>;

const SUPPORTED_PRIMITIVES = new Set(['string', 'integer', 'number', 'boolean']);

const collectFields = (schema: JsonSchema | null | undefined, prefix = ''): ConditionFieldDefinition[] => {
  if (!schema || schema.type !== 'object' || !schema.properties) {
    return [];
  }

  const fields: ConditionFieldDefinition[] = [];
  Object.entries(schema.properties).forEach(([name, propertySchema]: [string, any]) => {
    const path = prefix ? `${prefix}.${name}` : name;
    const description = propertySchema?.description;
    const type = propertySchema?.type;

    if (SUPPORTED_PRIMITIVES.has(type)) {
      fields.push({
        path,
        label: path,
        type,
        description,
      });
      return;
    }

    if (type === 'object') {
      fields.push(...collectFields(propertySchema, path));
      return;
    }

    if (type !== 'array' || !propertySchema?.items) {
      return;
    }

    const itemType = propertySchema.items.type;
    if (SUPPORTED_PRIMITIVES.has(itemType)) {
      fields.push({
        path,
        label: path,
        type: 'array',
        itemType,
        description,
      });
      return;
    }

    if (itemType === 'object') {
      fields.push(...collectFields(propertySchema.items, `${path}[]`));
    }
  });

  return fields;
};

export const extractConditionFieldsFromSchemas = (
  schemas: Array<JsonSchema | null | undefined>
): ConditionFieldDefinition[] => {
  const fieldMap = new Map<string, ConditionFieldDefinition>();
  const conflictingPaths = new Set<string>();

  schemas.forEach((schema) => {
    collectFields(schema).forEach((field) => {
      const existing = fieldMap.get(field.path);
      if (!existing) {
        fieldMap.set(field.path, field);
        return;
      }

      if (existing.type !== field.type || existing.itemType !== field.itemType) {
        conflictingPaths.add(field.path);
      }
    });
  });

  return Array.from(fieldMap.values())
    .filter((field) => !conflictingPaths.has(field.path))
    .sort((left, right) => left.path.localeCompare(right.path));
};
